import { motion, AnimatePresence } from "motion/react";
import { useState, useEffect } from "react";
import { 
  X, 
  Navigation, 
  MapPin, 
  Clock, 
  Truck,
  Phone,
  AlertCircle,
  CheckCircle2
} from "lucide-react";

interface LiveTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  delivery: {
    batchId: string;
    vehicleNumber: string;
    driverName: string;
    arrivalTime: string;
    distance: string;
    status: "in-transit" | "delivered" | "pending";
    sourceLocation?: string;
    destinationLocation?: string;
  } | null;
  onCallDriver: () => void;
}

export default function LiveTrackingModal({
  isOpen,
  onClose,
  delivery,
  onCallDriver,
}: LiveTrackingModalProps) {
  const [progress, setProgress] = useState(65);
  const [timeRemaining, setTimeRemaining] = useState("18 mins");

  useEffect(() => {
    if (!isOpen) return;
    
    // Simulate live tracking updates
    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + Math.random() * 2;
        return newProgress >= 100 ? 100 : newProgress;
      });
      
      // Update time remaining
      const mins = Math.max(1, Math.floor((100 - progress) / 5));
      setTimeRemaining(`${mins} mins`);
    }, 3000);

    return () => clearInterval(interval);
  }, [isOpen, progress]);

  if (!delivery) return null;

  const isDelivered = delivery.status === "delivered" || progress >= 100;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Bottom Sheet Modal */}
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center md:p-4 pointer-events-none pb-16 md:pb-0">
            <motion.div
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "100%" }}
              transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              className="bg-white rounded-t-3xl md:rounded-2xl w-full md:max-w-2xl max-h-[85vh] md:max-h-[90vh] overflow-hidden pointer-events-auto flex flex-col"
              style={{ boxShadow: "0 -10px 40px rgba(0, 0, 0, 0.15), 0 20px 50px rgba(0, 0, 0, 0.3)" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Handle (Mobile Only) */}
              <div className="md:hidden flex justify-center py-3 bg-white">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
                <div>
                  <h2 className="text-slate-900" style={{ fontSize: "20px", fontWeight: "700" }}>
                    Live Tracking
                  </h2>
                  <p className="text-slate-500" style={{ fontSize: "14px" }}>
                    {delivery.vehicleNumber}
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </motion.button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto" style={{ paddingBottom: "80px" }}>
                <div className="p-5 space-y-5">
                  
                  {/* Map Placeholder */}
                  <div className="relative bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl overflow-hidden" style={{ height: "280px" }}>
                    {/* Simulated Map Background */}
                    <div className="absolute inset-0 opacity-20">
                      <svg width="100%" height="100%" viewBox="0 0 400 280" fill="none">
                        <path d="M0 140 Q100 100, 200 120 T400 140" stroke="#3B82F6" strokeWidth="3" fill="none" strokeDasharray="10,5" opacity="0.3"/>
                        <path d="M0 140 Q100 100, 200 120 T280 140" stroke="#3B82F6" strokeWidth="4" fill="none"/>
                      </svg>
                    </div>
                    
                    {/* Source Marker */}
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-8 left-8"
                    >
                      <div className="relative">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                          <MapPin className="w-5 h-5 text-white" />
                        </div>
                        <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white px-2 py-1 rounded shadow-md text-xs font-semibold">
                          Plant
                        </div>
                      </div>
                    </motion.div>

                    {/* Destination Marker */}
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.1 }}
                      className="absolute top-8 right-8"
                    >
                      <div className="relative">
                        <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                          <MapPin className="w-5 h-5 text-white" />
                        </div>
                        <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white px-2 py-1 rounded shadow-md text-xs font-semibold">
                          Site
                        </div>
                      </div>
                    </motion.div>

                    {/* Moving Vehicle */}
                    <motion.div
                      initial={{ left: "10%" }}
                      animate={{ 
                        left: isDelivered ? "85%" : `${progress}%`,
                        top: ["30%", "35%", "30%"]
                      }}
                      transition={{ 
                        left: { duration: 2, ease: "easeInOut" },
                        top: { duration: 3, repeat: Infinity, ease: "easeInOut" }
                      }}
                      className="absolute"
                      style={{ top: "50%", transform: "translate(-50%, -50%)" }}
                    >
                      <div className="relative">
                        <motion.div
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center shadow-2xl"
                        >
                          <Truck className="w-6 h-6 text-white" />
                        </motion.div>
                        {/* Pulse Effect */}
                        <motion.div
                          animate={{ scale: [1, 1.8], opacity: [0.6, 0] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="absolute inset-0 bg-blue-500 rounded-full"
                        />
                      </div>
                    </motion.div>

                    {/* Live Badge */}
                    <div className="absolute top-4 left-1/2 -translate-x-1/2">
                      <div className="bg-white px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2">
                        <motion.div
                          animate={{ opacity: [1, 0.3, 1] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                          className="w-2 h-2 bg-red-500 rounded-full"
                        />
                        <span className="text-slate-900 font-semibold" style={{ fontSize: "12px" }}>
                          LIVE
                        </span>
                      </div>
                    </div>

                    {/* Distance Info */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                      <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                        <span className="text-slate-900 font-semibold" style={{ fontSize: "14px" }}>
                          {delivery.distance}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-600" style={{ fontSize: "14px", fontWeight: "600" }}>
                        Delivery Progress
                      </span>
                      <span className="text-blue-500" style={{ fontSize: "14px", fontWeight: "700" }}>
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: "0%" }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                      />
                    </div>
                  </div>

                  {/* Status Cards */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-blue-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-4 h-4 text-blue-500" />
                        <span className="text-blue-900" style={{ fontSize: "12px", fontWeight: "600" }}>
                          ETA
                        </span>
                      </div>
                      <div className="text-slate-900" style={{ fontSize: "20px", fontWeight: "700" }}>
                        {isDelivered ? "Arrived" : timeRemaining}
                      </div>
                      <div className="text-slate-600" style={{ fontSize: "11px" }}>
                        Expected arrival
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Navigation className="w-4 h-4 text-green-500" />
                        <span className="text-green-900" style={{ fontSize: "12px", fontWeight: "600" }}>
                          Speed
                        </span>
                      </div>
                      <div className="text-slate-900" style={{ fontSize: "20px", fontWeight: "700" }}>
                        {isDelivered ? "0" : "45"} <span style={{ fontSize: "14px" }}>km/h</span>
                      </div>
                      <div className="text-slate-600" style={{ fontSize: "11px" }}>
                        Current speed
                      </div>
                    </div>
                  </div>

                  {/* Status Update */}
                  {isDelivered ? (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-green-50 border-2 border-green-200 rounded-xl p-4"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-green-900 mb-1" style={{ fontWeight: "700" }}>
                            Delivery Completed
                          </div>
                          <p className="text-green-700" style={{ fontSize: "13px" }}>
                            The concrete has been successfully delivered to the destination.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      animate={{ opacity: [1, 0.7, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4"
                    >
                      <div className="flex items-start gap-3">
                        <Truck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-blue-900 mb-1" style={{ fontWeight: "700" }}>
                            Vehicle In Transit
                          </div>
                          <p className="text-blue-700" style={{ fontSize: "13px" }}>
                            {delivery.driverName} is on the way. Estimated arrival: {delivery.arrivalTime}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Route Details */}
                  <div>
                    <h3 className="text-slate-900 mb-3" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Route Details
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-4 h-4 text-green-600" />
                        </div>
                        <div className="flex-1 pt-1">
                          <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>From</div>
                          <div className="text-slate-900" style={{ fontWeight: "600" }}>
                            {delivery.sourceLocation || "RMC Plant - Sector 18"}
                          </div>
                        </div>
                      </div>
                      
                      <div className="ml-4 border-l-2 border-dashed border-slate-200 h-6" />
                      
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-4 h-4 text-red-600" />
                        </div>
                        <div className="flex-1 pt-1">
                          <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>To</div>
                          <div className="text-slate-900" style={{ fontWeight: "600" }}>
                            {delivery.destinationLocation || "Construction Site - Andheri West"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="p-5 border-t border-slate-100 bg-white flex-shrink-0">
                <div className="grid grid-cols-2 gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      onCallDriver();
                      onClose();
                    }}
                    className="py-3.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl transition-all font-semibold flex items-center justify-center gap-2"
                  >
                    <Phone className="w-4 h-4" />
                    Call Driver
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onClose}
                    className="py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-xl transition-all font-semibold"
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
